

/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase, SUPABASE_URL } from '../lib/supabase';
import { SiteSettings } from '../types';
import { safeQuery } from '../utils/supabaseHelpers';


interface SettingsContextType {
    settings: SiteSettings | null;
    isLoading: boolean;
    refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

const SETTINGS_CACHE_KEY = 'squeaky_settings_cache';
const CACHE_DURATION = 60 * 60 * 1000; // 1 hour

const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Tiếp tục dù lỗi
        img.src = src;
    });
};

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<SiteSettings | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const fetchSettings = async () => {
        if (SUPABASE_URL.includes('placeholder')) {
            setIsLoading(false);
            return;
        }

        // Try cache first
        const cached = localStorage.getItem(SETTINGS_CACHE_KEY);
        if (cached) {
            try {
                const { data, timestamp } = JSON.parse(cached);
                if (Date.now() - timestamp < CACHE_DURATION) {
                    setSettings(data);
                    if (data.hero?.image) {
                        await preloadImage(data.hero.image);
                    }
                    setIsLoading(false);
                    // Background refresh if cache is older than 5 mins but less than 1 hour? 
                    // For now, simple cache logic is fine.
                    return;
                }
            } catch (e) {
                console.error('Error parsing settings cache', e);
                localStorage.removeItem(SETTINGS_CACHE_KEY);
            }
        }

        try {
            const result = await safeQuery(
                async () => supabase.from('site_settings').select('key, value'),
                {
                    timeout: 8000,
                    retry: true,
                    onError: (err) => console.error('Settings query error:', err.message)
                }
            );

            const { data, error } = result;

            if (!error && data) {
                const config: Record<string, unknown> = {};
                data.forEach(item => { config[item.key] = item.value; });
                const typedSettings = config as unknown as SiteSettings;
                setSettings(typedSettings);

                localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify({
                    data: typedSettings,
                    timestamp: Date.now()
                }));

                if (typedSettings.hero?.image) {
                    await preloadImage(typedSettings.hero.image);
                }
            } else if (error) {
                console.error("Error fetching global settings:", error);
            }
        } catch (e) {
            console.error("Error fetching global settings:", e);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, []);

    return (
        <SettingsContext.Provider value={{ settings, isLoading, refreshSettings: fetchSettings }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
